/** Copyright (c) 2018 T. Rowe Price. All rights reserved. */

import './PerformancePage.scss';

import { bool, string, object, array } from 'prop-types';
import React, { useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import PrintButton from 'components/shared/PrintButton/PrintButton';
import ActivitySummary from 'components/layouts/Activity/ActivitySummary';
import PortfolioGrowth from 'components/layouts/Growth/PortfolioGrowth';
import Performance from 'components/layouts/Performance/Performance';
import PerformanceTable from 'components/layouts/Performance/PerformanceTable';
import { PageHeader } from 'components/pages/Header/PageHeader';
import { SectionHeader } from 'components/pages/Header/SectionHeader';
import { SectionHeaderWithSeparator } from 'components/pages/Header/SectionHeaderWithSeparator';
import {isBrokerageInceptionDateBefore2019, getPurgedAccounts} from 'state/account/AccountSelectors';
import { getRouter } from 'state/router/RouterSelectors';
import {
  brokerageAppMessaging,
} from 'services/Content';
import { isDesktopLayout } from 'state/view/ViewSelectors';
import { scrollPage } from 'support/LayoutUtils';
import AccountFilterModal from 'components/layouts/Bar/Selection/AccountFilter/AccountFilterModal';
import PerformancePeriodFilter from 'components/layouts/Performance/PerformancePeriodFilter';
import ActivitySummaryBreakdownGrid from 'components/layouts/Activity/ActivitySummaryBreakdownGrid';
import ActivitySummaryDataProvider from 'components/layouts/Activity/ActivitySummaryDataProvider';
import RateOfReturnDataProvider from 'components/layouts/Performance/RateOfReturnDataProvider';
import RateOfReturnBreakdownGrid from 'components/layouts/Performance/RateOfReturnBreakdownGrid';
import {isAdvicePerformancePageEnabled, isPerformancePageLabelsEnabled} from 'services/FeatureFlags';
import ActivitySummaryTable from 'components/layouts/Activity/ActivitySummaryTable';
import ClosedAccountsGrid from 'components/layouts/Performance/ClosedAccountsGrid';
import AccountTypeFilter from 'components/layouts/Bar/Selection/AccountFilter/AccountTypeFilter';
import {
  ACTIVITY_SUMMARY_FILTER_TYPE,
  getRateOfReturnOptions,
  getSelectedProductTypeForFilterType,
  hasActivePlus,
  hasBrokerage,
  hasBrokeragePerformanceOnly,
  PORTFOLIO_GROWTH_FILTER_TYPE,
  PRR_FILTER_TYPE,
  showPerformancePageActivitySummaryBrokerageWarning,
  showPerformancePagePortfolioGrowthBrokerageWarning,
  showWarning,
} from 'state/performance/PerformancePageSelectors';
import {
  getActivitySummaryFilter,
  getFormattedDateRangeForActivitySummary
} from 'state/performance/ActivitySummaryFilterSelectors';
import {
  getPortfolioGrowthFilter,
  getFormattedDateRangeForPortfolioGrowth
} from 'state/performance/PortfolioGrowthFilterSelectors';
import {
  getAsOfDateForRateOfReturn,
  getInceptionDateForRateOfReturn
} from 'state/performance/RateOfReturnFilterSelectors';
import {yyyymmdd2mmmddyyyy} from 'services/Dates';
import {formattedFirstAvailableBrokerageActivityDate} from "components/pages/Performance/Utilities/PerformancePageUtils";

const PerformancePage = ({
  router,
  isDesktopLayout,
  portfolioGrowthSelectedProductType,
  activitySummarySelectedProductType,
  showNewGrids,
  rateOfReturnAsOfDate,
  rateOfReturnInceptionDate,
  showWarningForActivitySummary,
  showWarningForActivitySummaryNew,
  showWarningForPortfolioGrowth,
  showWarningForPortfolioGrowthNew,
  hasBrokerage,
  hasBrokeragePerformanceOnly,
  hasActivePlus,
  rateOfReturnOptions,
  portfolioGrowthDateRange,
  activitySummaryDateRange
}) => {
  // Routes the fragment to the ID, if present. The array passed as the second argument prevents the effect from being
  // run if the showPerformance value has not changed.

  useEffect(() => {
    const hash = router.location.hash;
    if (hash && hash === '#personal-rate-of-return') {
      const id = document.getElementById('personal-rate-of-return');
      window.scrollTo(0, scrollPage(id, 170)); // no offset necessary
    }
  }, [router.location.hash]);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 2000);
  };

  const ActivitySummaryBreakdownWithData = ActivitySummaryDataProvider(
    ActivitySummaryBreakdownGrid
  );
  const RateOfReturnBreakdownWithData = RateOfReturnDataProvider(
    RateOfReturnBreakdownGrid,
    false
  );
  const ClosedAccountsGridWithData = RateOfReturnDataProvider(
    ClosedAccountsGrid,
    true
  );

  const rateOfReturnProductType =
    rateOfReturnOptions.length === 1 ? rateOfReturnOptions[0] : undefined;

  return (
    <div className="performance-page">
      <PageHeader title="Performance">
        <div className="account-filter-and-print-container">
          <AccountFilterModal />
          <div className="print-container">
            <PrintButton
              handlePrint={handlePrint}
              isDesktopLayout={isDesktopLayout}
              className="performance-header"
            />
          </div>
        </div>
      </PageHeader>
      <div className="summary-container">
        <div className="activity-container">
          <SectionHeader
            title="Portfolio Growth"
            fromDate={portfolioGrowthDateRange.fromDate}
            toDate={portfolioGrowthDateRange.toDate}
          />
          <div className="filter-and-warning-container">
            <div className="performance-selection-bar">
              <AccountTypeFilter filterType={PORTFOLIO_GROWTH_FILTER_TYPE}/>
              <PerformancePeriodFilter
                filterType={PORTFOLIO_GROWTH_FILTER_TYPE}
              />
            </div>
            {(isPerformancePageLabelsEnabled() && showWarningForPortfolioGrowthNew && (portfolioGrowthDateRange.fromDate < formattedFirstAvailableBrokerageActivityDate)) ? (
              <div className="performance-brokerage-app-messaging">
                <p>
                  Brokerage accounts do not display activity information prior to January 1, 2019.
                </p>
              </div>
            ) : (
              <span>
            {!isPerformancePageLabelsEnabled() && showWarningForPortfolioGrowth && (
              <div className="performance-brokerage-app-messaging">
              <p>
            {brokerageAppMessaging(
              hasBrokerage,
              hasActivePlus,
              portfolioGrowthSelectedProductType
              )}
          </p>
        </div>
        )}
              </span>
              )}
          </div>
          <div className="pure-g keep-together">
            <PortfolioGrowth />
          </div>
          <SectionHeaderWithSeparator
            title="Activity Summary"
            fromDate={activitySummaryDateRange.fromDate}
            toDate={activitySummaryDateRange.toDate}
            dateSeparator="through"
          />
          <div className="filter-and-warning-container">
            <div className="performance-selection-bar">
              <AccountTypeFilter filterType={ACTIVITY_SUMMARY_FILTER_TYPE}/>
              <PerformancePeriodFilter
                filterType={ACTIVITY_SUMMARY_FILTER_TYPE}
              />
            </div>
            {(isPerformancePageLabelsEnabled() && showWarningForActivitySummaryNew && (activitySummaryDateRange.fromDate < formattedFirstAvailableBrokerageActivityDate)) ? (
              <div className="performance-brokerage-app-messaging">
                <p>
                  Brokerage accounts do not display activity information prior to January 1, 2019.
                </p>
              </div>
            ) : (
              <span>
            {!isPerformancePageLabelsEnabled() && showWarningForActivitySummary && (
              <div className="performance-brokerage-app-messaging">
              <p>
            {brokerageAppMessaging(
              hasBrokerage,
              hasActivePlus,
              activitySummarySelectedProductType
              )}
          </p>
    </div>
  )}
              </span>
              )}
          </div>
          <ActivitySummary />
        </div>
      </div>
      <div className="pure-g keep-together">
        <SectionHeader
          title="Activity Summary Breakdown"
          fromDate={activitySummaryDateRange.fromDate}
          toDate={activitySummaryDateRange.toDate}
        />
        {showNewGrids ? (
          <ActivitySummaryBreakdownWithData />
        ) : (
          <ActivitySummaryTable />
        )}
      </div>
      <div>
        <SectionHeader
          title="Personal Rate of Return"
          fromDate={rateOfReturnInceptionDate}
          toDate={rateOfReturnAsOfDate}
        />
        <div className="filter-and-warning-container">
          {!hasBrokeragePerformanceOnly &&
            (rateOfReturnProductType ? (
              <p className="one-filter-option">{rateOfReturnProductType}</p>
            ) : (
              <div className="performance-selection-bar">
                <AccountTypeFilter filterType={PRR_FILTER_TYPE}/>
              </div>
            ))}
        </div>
        <div className="pure-g keep-together" id="personal-rate-of-return">
          <Performance />
        </div>
      </div>
      <div className="pure-g keep-together">
        <SectionHeader
          title="Personal Rate of Return Breakdown"
          fromDate={rateOfReturnInceptionDate}
          toDate={rateOfReturnAsOfDate}
        />
        {showNewGrids && (
          <Fragment>
            <RateOfReturnBreakdownWithData />
            <ClosedAccountsGridWithData />
          </Fragment>
        )}
        {!showNewGrids && <PerformanceTable />}
      </div>
    </div>
  );
};

PerformancePage.propTypes = {
  showNewGrids: bool,
  portfolioGrowthFilter: object.isRequired,
  activitySummaryFilter: object.isRequired,
  isDesktopLayout: bool,
  rateOfReturnAsOfDate: string,
  rateOfReturnAInceptionDate: string,
  showWarningForActivitySummary: bool,
  showWarningForPortfolioGrowth: bool,
  hasBrokerage: bool,
  hasBrokeragePerformanceOnly: bool,
  hasActivePlus: bool,
  rateOfReturnOptions: array
};

// TODO : remove showNewGrids after improvements have been made to AG grids for 529 only users
const mapStateToProps = (state) => {
  return {
    showNewGrids: isAdvicePerformancePageEnabled(),
    portfolioGrowthFilter: getPortfolioGrowthFilter(state),
    activitySummaryFilter: getActivitySummaryFilter(state),
    isDesktopLayout: isDesktopLayout(state),
    purgedAccounts: getPurgedAccounts(state),
    rateOfReturnAsOfDate: yyyymmdd2mmmddyyyy(getAsOfDateForRateOfReturn(state)),
    rateOfReturnInceptionDate: yyyymmdd2mmmddyyyy(
      getInceptionDateForRateOfReturn(state)
    ),
    showWarningForActivitySummaryNew: showPerformancePageActivitySummaryBrokerageWarning(
      state,
      ACTIVITY_SUMMARY_FILTER_TYPE
    ),
    showWarningForPortfolioGrowthNew: showPerformancePagePortfolioGrowthBrokerageWarning(
      state,
      PORTFOLIO_GROWTH_FILTER_TYPE
    ),
    showWarningForActivitySummary: showWarning(
      state,
      ACTIVITY_SUMMARY_FILTER_TYPE
    ),
    showWarningForPortfolioGrowth: showWarning(
      state,
      PORTFOLIO_GROWTH_FILTER_TYPE
    ),
    hasBrokerage: hasBrokerage(state),
    isBrokerageInceptionDateBefore2019: isBrokerageInceptionDateBefore2019(state),
    hasBrokeragePerformanceOnly: hasBrokeragePerformanceOnly(state),
    hasActivePlus: hasActivePlus(state),
    rateOfReturnOptions: getRateOfReturnOptions(state),
    portfolioGrowthDateRange: getFormattedDateRangeForPortfolioGrowth(state),
    activitySummaryDateRange: getFormattedDateRangeForActivitySummary(state),
    activitySummarySelectedProductType: getSelectedProductTypeForFilterType(
      state,
      ACTIVITY_SUMMARY_FILTER_TYPE
    ),
    portfolioGrowthSelectedProductType: getSelectedProductTypeForFilterType(
      state,
      PORTFOLIO_GROWTH_FILTER_TYPE
    ),
    router: getRouter(state)
  };
};

export default withRouter(connect(mapStateToProps)(PerformancePage));
